import { Payee, PayeeType, ScheduleType } from '@bybeam/platform-types';
import Link from '@platform-ui-common/components/Link/index.js';
import useProgram from '@platform-ui-common/hooks/contexts/useProgram.js';
import { isThirdPartyUser } from '@platform-ui-common/utilities/applicantType.js';
import { formatCurrency } from '@platform-ui-common/utilities/format.js';
import {
  PaymentMethodDisplay,
  PaymentScheduleDisplay,
  displayPaymentDetails,
  findLatestPayment,
  getPaymentStatus,
} from '@platform-ui-common/utilities/payment.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import useCase from '@platform-ui/case/hooks/useCase.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { Card, DataList, Flex, Text, Tooltip } from '@radix-ui/themes';
import { useMemo } from 'react';
import PanelWrapper from './PanelWrapper.js';

export default function FulfillmentsPanel() {
  const program = useProgram();
  const { case_ } = useCase();
  const { user } = useAuth();

  if (!program || !case_) return null;

  const payee = useMemo(() => {
    return user && isThirdPartyUser(user?.core)
      ? ({ ...user.core, payeeType: PayeeType.User } as Payee)
      : undefined;
  }, [user]);

  const fulfillments = useMemo(() => {
    if (!case_?.fulfillments) return [];

    return case_.fulfillments
      .filter((fulfillment) => displayPaymentDetails({ program, case: case_, fulfillment, payee }))
      .map(({ payments, ...rest }) => ({
        ...rest,
        payments: payee ? payments?.filter((payment) => payment.payee.id === payee.id) : payments,
      }))
      .filter((fulfillment) => (fulfillment.payments?.length ?? 0) > 0);
  }, [case_, program, payee]);

  if (!fulfillments?.length) return null;
  return (
    <PanelWrapper title="Payments">
      <Flex direction="column" gap="2">
        {fulfillments.map((fulfillment) => {
          const {
            payments = [],
            payments: [firstPayment],
            paymentPattern,
            scheduleType,
          } = fulfillment;
          const latestPayment = findLatestPayment(payments);
          const payment = latestPayment ?? firstPayment;
          return (
            <Card key={fulfillment.id}>
              <DataList.Root orientation={{ initial: 'vertical', sm: 'horizontal' }}>
                <DataList.Item align="start">
                  <DataList.Label minWidth="130px">Display ID</DataList.Label>
                  <DataList.Value>
                    <Tooltip content="View Payment Details">
                      <Text>
                        <Link
                          to={makeRoute(ApplicantRoutes.FULFILLMENT_DETAILS, {
                            caseId: case_.id,
                            fulfillmentId: fulfillment.id,
                          })}
                        >
                          {fulfillment.displayId}
                        </Link>
                      </Text>
                    </Tooltip>
                  </DataList.Value>
                </DataList.Item>
                <DataList.Item align="start">
                  <DataList.Label minWidth="130px">Amount</DataList.Label>
                  <DataList.Value>{formatCurrency(payment.amount, true)}</DataList.Value>
                </DataList.Item>
                <DataList.Item align="start">
                  <DataList.Label minWidth="130px">Payment Method</DataList.Label>
                  <DataList.Value>
                    {PaymentMethodDisplay[payment.method]}
                    {scheduleType === ScheduleType.Recurring &&
                      !!paymentPattern &&
                      ` - ${PaymentScheduleDisplay[paymentPattern.pattern]}`}
                  </DataList.Value>
                </DataList.Item>
                <DataList.Item align="start">
                  <DataList.Label minWidth="130px">Payment Status</DataList.Label>
                  <DataList.Value>{getPaymentStatus(program, fulfillment) ?? '-'}</DataList.Value>
                </DataList.Item>
                {scheduleType === ScheduleType.Recurring && (
                  <DataList.Item align="start">
                    <DataList.Label minWidth="130px">Number of Payments</DataList.Label>
                    <DataList.Value>{paymentPattern?.count ?? 0} payments</DataList.Value>
                  </DataList.Item>
                )}
              </DataList.Root>
            </Card>
          );
        })}
      </Flex>
    </PanelWrapper>
  );
}
